import { Card, wantsCardImage } from '@lingoping/core';
import { StorageManager } from '../storage/index.js';
import { bundledReferenceSlug, deckImageSlug, storedDeckImageUrl } from '../utils/images.js';

const MIGRATED_KEY = 'lingoping_bundled_pictures_migrated_v1';

/**
 * The card with its picture switched to the one in storage, or null when it needs no change.
 * Two kinds of cards qualify: those pointing at a packaged picture (`bundled:de-basic/...`), and
 * noun/verb cards made from the old bundled deck that never got a picture reference at all (made
 * before the pictures were packaged; every noun and verb of that deck has a stored picture).
 */
export function withStoredPicture(card: Card, supabaseUrl?: string): Card | null {
  const url = card.image_url;
  const bundledSlug = url ? bundledReferenceSlug(url) : undefined;
  const fromOldDeck =
    !bundledSlug &&
    card.linguistic_meta?.content_source === 'deck' &&
    wantsCardImage(card.part_of_speech) &&
    (!url || url.startsWith('https://image.pollinations.ai/'));
  const slug = bundledSlug ?? (fromOldDeck ? deckImageSlug(card.lemma ?? card.front_text) : '');
  if (!slug) return null;
  return { ...card, image_url: storedDeckImageUrl(slug, supabaseUrl) };
}

/**
 * Once per user: switches the picture of every card that points at a packaged picture to the
 * stored one (see withStoredPicture), as a change of each card's picture like any other, so it
 * reaches the account. Returns how many cards changed. A card that is not migrated yet (or comes back from another device that
 * still has the old reference) still shows its picture: resolveImageUrl maps `bundled:` to the
 * same storage URL.
 */
export async function migrateBundledPictures(userId: string): Promise<number> {
  if (!userId) return 0;
  const doneKey = `${MIGRATED_KEY}_${userId}`;
  if (await StorageManager.getLocalValue<boolean>(doneKey)) return 0;

  let changed = 0;
  for (const card of await StorageManager.getCards()) {
    const migrated = withStoredPicture(card);
    if (!migrated) continue;
    await StorageManager.updateCardContent(card.id, { image_url: migrated.image_url });
    changed++;
  }
  await StorageManager.setLocalValue(doneKey, true);
  return changed;
}
